import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { AvailableTabProperties } from '../../../../../interface/device-management.interface';
import TabsContainer from '../../../../../shared-components/tabs-container/TabsContainer';

import Alerts from './Alerts';
import ChargingSchedules from './ChargingSchedules';
import CommercialSessionTable from './CommercialSessionTable';
import GuestCharging from './GuestCharging';
import MaintenanceSchedule from './MaintenanceSchedule';
import OcppLogs from './OcppLogs';
import RfidCards from './RfidCards';
import Timeline from './Timeline';
import Transactions from './Transactions';

/**
 * Component to display device button tabs
 * @returns {any} The result
 */
function DeviceDetailsBottomContainer() {
  const { t, i18n } = useTranslation('devices');
  const { id } = useParams();
  const location = useLocation();
  const searchParameters = new URLSearchParams(location.search);

  const isPersonalCharger =
    location.pathname === `/ev-admin/charger-management/${id}/device-details` &&
    searchParameters.get('type') === 'personal-charger';
  const [deviceDetailsTabs, setDeviceDetailsTabs] = useState<AvailableTabProperties[]>([
    // {
    //   label: t('deviceData', 'Charger Data'),
    //   key: 'device-data',
    //   isActive: true,
    // },
    {
      label: t('liveSessions', 'Live Sessions'),
      key: 'live-sessions',
      isActive: true,
      allowed:
        location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    },
    {
      label: t('timeline', 'Timeline'),
      key: 'timeline',
      isActive: false,
      allowed:
        location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    },
    {
      label: t('transactions', 'Transactions'),
      key: 'transactions',
      isActive: false,
      allowed:
        location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    },
    // {
    //   label: t('guestCharging', 'Guest Charging'),
    //   key: 'guest-charging',
    //   isActive: false,
    //   allowed:
    //     location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    // },
    {
      label: t('maintenanceSchedules', 'Maintenance Schedules'),
      key: 'upcoming-maintenance-schedules',
      isActive: false,
      allowed:
        location.pathname.includes('ev-admin/') &&
        !location.pathname.includes('ev-device-hub') &&
        !isPersonalCharger,
    },
    {
      label: t('oCPPLogs', 'OCPP Logs'),
      key: 'ocpp-logs',
      isActive: false,
      allowed: location.pathname.includes('ev-device-hub/'),
    },
    // {
    //   label: t('chargingSchedules', 'Charging Schedules'),
    //   key: 'upcoming-charging-schedules',
    //   isActive: false,
    //   allowed:
    //     location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    // },
    // {
    //   label: t('alerts', 'Alerts'),
    //   key: 'alerts',
    //   isActive: false,
    //   allowed:
    //     location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    // },
    // {
    //   label: t('localAuthList', 'Local Auth List'),
    //   key: 'rfid-cards-associated',
    //   isActive: false,
    //   allowed:
    //     location.pathname.includes('ev-admin/') && !location.pathname.includes('ev-device-hub'),
    // },
  ]);

  useEffect(() => {
    setDeviceDetailsTabs((previousTabs) =>
      previousTabs.map((tab) => {
        switch (tab.key) {
          case 'device-data':
            return { ...tab, label: t('deviceData', 'Charger Data') };
          case 'live-sessions':
            return { ...tab, label: t('liveSessions', 'Live Sessions') };
          case 'timeline':
            return { ...tab, label: t('timeline', 'Timeline') };
          case 'transactions':
            return { ...tab, label: t('transactions', 'Transactions') };
          case 'guest-charging':
            return { ...tab, label: t('guestCharging', 'Guest Charging') };
          case 'upcoming-maintenance-schedules':
            return { ...tab, label: t('maintenanceSchedules', 'Maintenance Schedules') };
          case 'ocpp-logs':
            return { ...tab, label: t('oCPPLogs', 'OCPP Logs') };
          case 'upcoming-charging-schedules':
            return { ...tab, label: t('chargingSchedules', 'Charging Schedules') };
          case 'alerts':
            return { ...tab, label: t('alerts', 'Alerts') };
          case 'rfid-cards-associated':
            return { ...tab, label: t('localAuthList', 'Local Auth List') };
          default:
            return tab;
        }
      }),
    );
  }, [t]);

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleSwitchTab = (key: string) => {
    setDeviceDetailsTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  React.useEffect(() => {
    // Find the first tab that is allowed
    const allowedTabs = deviceDetailsTabs.filter((tab) => tab.allowed !== false);
    const hasActiveAllowedTab = allowedTabs.some((tab) => tab.isActive);
    if (!hasActiveAllowedTab && allowedTabs.length > 0) {
      const firstAllowedKey = allowedTabs[0].key;
      setDeviceDetailsTabs((previous) =>
        previous.map((tab) => ({
          ...tab,
          isActive: tab.key === firstAllowedKey,
        })),
      );
    }
  }, [location.pathname]);

  return (
    <div className="device-details-bottom-wrap" dir={i18n.dir()}>
      <TabsContainer
        containerTabs={deviceDetailsTabs.filter((tab) => tab.allowed !== false)}
        handleSwitchTab={handleSwitchTab}
      />
      <div className="device-details-bottom-container">
        {(() => {
          const activeTab = deviceDetailsTabs.find((detail) => detail.isActive);
          let dataToShow = null;
          if (activeTab) {
            switch (activeTab.key) {
              // case 'device-data':
              //   dataToShow = <DeviceData />;
              //   break;
              case 'alerts':
                dataToShow = <Alerts />;
                break;
              case 'transactions':
                dataToShow = <Transactions />;
                break;
              case 'guest-charging':
                dataToShow = <GuestCharging />;
                break;
              case 'upcoming-charging-schedules':
                dataToShow = <ChargingSchedules />;
                break;
              case 'rfid-cards-associated':
                dataToShow = <RfidCards />;
                break;
              case 'upcoming-maintenance-schedules':
                dataToShow = <MaintenanceSchedule />;
                break;
              case 'ocpp-logs':
                dataToShow = <OcppLogs />;
                break;
              case 'timeline':
                dataToShow = <Timeline />;
                break;
              case 'live-sessions':
                dataToShow = <CommercialSessionTable />;
                break;
              default:
                dataToShow = null;
            }
          }
          return dataToShow;
        })()}
      </div>
    </div>
  );
}

export default DeviceDetailsBottomContainer;
